import { useLazyQuery, useQuery } from "@apollo/client";
import { IonCol, IonGrid, IonRow } from "@ionic/react";
import { motion } from "framer-motion";
import { PropsWithChildren, ReactNode, RefObject, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useIntl } from "react-intl";

import AccountDropdown from "@components/account/AccountDropdown";
import JourneySelector from "@components/JourneySelector";
import AppPageWithMenu from "@components/pageTypes/AppPageWithMenu";
import styles from "@components/pageTypes/Page.module.css";
import {
  GetClientWithJourneysDocument,
  GetClientWithJourneysQuery,
  GetClientWithJourneysQueryVariables,
  GetJourneyWithOwnerClientDocument,
  GetJourneyWithOwnerClientQuery,
  GetJourneyWithOwnerClientQueryVariables
} from "@typing/Generated";
import { slideHorizontalAnimation, slideVerticalAnimation } from "@utils/animationUtils";
import { isMobile } from "@utils/platformUtils";

type Props = PropsWithChildren<{
  animated?: "right" | "up";
  contentRef?: RefObject<HTMLIonContentElement>;
  gradient?: boolean;
  header?: ReactNode;
  helmetKey: string;
  journeyId: string;
  mobileFullscreen?: boolean;
}>;

const ExpertJourneyPage = ({
  animated,
  children,
  contentRef,
  gradient = true,
  helmetKey,
  journeyId,
  mobileFullscreen = false
}: Props) => {
  const intl = useIntl();

  const journeyWithOwnerQuery = useQuery<GetJourneyWithOwnerClientQuery, GetJourneyWithOwnerClientQueryVariables>(
    GetJourneyWithOwnerClientDocument,
    {
      variables: {
        id: journeyId
      }
    }
  );

  const [getClient, clientQuery] = useLazyQuery<GetClientWithJourneysQuery, GetClientWithJourneysQueryVariables>(
    GetClientWithJourneysDocument,
    {
      variables: {
        id: journeyId
      }
    }
  );

  useEffect(() => {
    const clientId = journeyWithOwnerQuery.data?.journey.owner.id;
    if (clientId) {
      getClient({
        variables: {
          id: clientId
        }
      });
    }
  }, [getClient, journeyWithOwnerQuery.data?.journey.owner.id]);

  const pageVariants = animated === "right" ? slideHorizontalAnimation("right") : slideVerticalAnimation("up");

  const pageContainerClassNames = [styles.clientPageContainer, styles.desktopPadding, styles.mobilePadding];
  if (mobileFullscreen) pageContainerClassNames.push(styles.mobileFullscreen);

  return (
    <AppPageWithMenu contentRef={contentRef} journeyId={journeyId}>
      <motion.div
        animate="animate"
        className={gradient ? styles.background : ""}
        exit="exit"
        initial="initial"
        transition={{ duration: 0.5, type: "tween" }}
        variants={animated && isMobile() ? pageVariants : undefined}
      >
        <div className={gradient ? styles.background : ""}>
          <Helmet>
            <title>{intl.formatMessage({ id: helmetKey })}</title>
          </Helmet>
          <div className={pageContainerClassNames.join(" ")}>
            <div className={`${mobileFullscreen ? "ion-hide-lg-down" : ""} ${styles.toolbar}`}>
              <IonGrid className="ion-no-padding">
                <IonRow className="ion-align-items-center">
                  <IonCol>
                    <div className={styles.toolbarLeft}>
                      <JourneySelector clientQuery={clientQuery} journeyId={journeyId} />
                    </div>
                  </IonCol>
                  <IonCol size="auto">
                    <div>
                      {journeyWithOwnerQuery.data?.journey.owner.id && <AccountDropdown journeyId={journeyId} />}
                    </div>
                  </IonCol>
                </IonRow>
              </IonGrid>
            </div>
            <div className={styles.clientPageContent}>{children}</div>
          </div>
        </div>
      </motion.div>
    </AppPageWithMenu>
  );
};

export default ExpertJourneyPage;
